/**
 * Auto-add the matching scented-oil product whenever a shopper picks an
 * Aroma/Scent on The 24/7 Room Diffuser.
 *
 * The Aroma picker on this store is rendered by a third-party product
 * customizer app ("Select your free scent" dropdown), not by Shopify's
 * native line-item-property block. Its radio inputs have no "name"
 * attribute (the app injects the properties[Scent] value into the
 * add-to-cart request itself), so we match the radio inputs directly by
 * their stable id prefix ("tcustomizer-form-field-").
 *
 * Product lookup strategy:
 *   1. STATIC_SCENT_MAP below is a snapshot of every product currently
 *      tagged "single_notes_collection" in the catalog (built from the
 *      Admin API), keyed by exact product title. This is the primary
 *      lookup and works even for products that are set to "Unlisted"
 *      (e.g. Olympus Mint) - unlisted products don't appear in the
 *      storefront search index, so a live search alone would miss them.
 *   2. If a selected scent isn't in the map (e.g. a brand new product
 *      added after this script was last updated), it falls back to a
 *      live lookup: search /search/suggest.json for a product whose
 *      title exactly matches the scent label, then fetch that product's
 *      /products/<handle>.js to get its variant id. This only finds
 *      products that are Active + listed, so a newly added scent should
 *      be published normally (not left Unlisted) for the fallback to
 *      pick it up automatically.
 *
 * Snapshot taken 2026-07-31. Includes a few scents not yet shown in the
 * live picker (Yarra Eucalyptus, Kerala Ginger, Sambava Vanilla, Colombo
 * Coconut) so they'll already work the moment they're added to the
 * picker - no script change needed for those. If new scents are added
 * to the catalog *after* this snapshot, the live fallback should still
 * catch them as long as they're published/listed normally.
 *
 * Note on pricing: this script adds the oil at its normal price. Per the
 * merchant, an existing discount rule in Shopify's back office already
 * handles making the first bottle free ("+ Your first FREE Aroma") - this
 * script does not touch price/discounting.
 */
(function () {
  var RADIO_ID_PREFIX = 'tcustomizer-form-field-';
  var QUANTITY = 1;

  var STATIC_SCENT_MAP = {
    'Provence Lavender (300ml)': 49249343963482,
    'Kyoto Jasmine (300ml)': 49252717101402,
    'Marrakesh Orange (300ml)': 49252743414106,
    'Sicilian Lemon (300ml)': 49275064484186,
    'Olympus Mint (300ml)': 49275072643418,
    'Comisa Rosemary (300ml)': 49275077067098,
    'Yarra Eucalyptus (300ml)': 49275099349338,
    'Kerala Ginger (300ml)': 49275125104986,
    'Sambava Vanilla (300ml)': 53688173723994,
    'Colombo Coconut (300ml)': 53688189878618
  };

  var lookupCache = {}; // scent label -> variant id (or null if not found)
  var inFlight = {}; // scent label -> true while an add is in progress

  function isAromaRadio(el) {
    return !!(
      el &&
      el.tagName === 'INPUT' &&
      el.type === 'radio' &&
      el.id &&
      el.id.indexOf(RADIO_ID_PREFIX) === 0
    );
  }

  function isAlreadyInCart(variantId) {
    return fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        return cart.items.some(function (item) { return item.variant_id === variantId; });
      })
      .catch(function (err) {
        console.error('[aroma-auto-add] could not read cart', err);
        return false;
      });
  }

  function findVariantIdForScent(scentLabel) {
    if (Object.prototype.hasOwnProperty.call(STATIC_SCENT_MAP, scentLabel)) {
      return Promise.resolve(STATIC_SCENT_MAP[scentLabel]);
    }

    if (Object.prototype.hasOwnProperty.call(lookupCache, scentLabel)) {
      return Promise.resolve(lookupCache[scentLabel]);
    }

    var searchUrl =
      '/search/suggest.json?q=' +
      encodeURIComponent(scentLabel) +
      '&resources[type]=product&resources[limit]=5&resources[options][fields]=title';

    return fetch(searchUrl)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var products =
          (data.resources && data.resources.results && data.resources.results.products) || [];
        var match =
          products.filter(function (p) { return p.title === scentLabel; })[0] || products[0];

        if (!match) {
          console.warn('[aroma-auto-add] no matching product found for scent:', scentLabel);
          lookupCache[scentLabel] = null;
          return null;
        }

        return fetch('/products/' + match.handle + '.js')
          .then(function (res) { return res.json(); })
          .then(function (product) {
            var variant = (product.variants && product.variants[0]) || null;
            var variantId = variant ? variant.id : null;
            lookupCache[scentLabel] = variantId;
            return variantId;
          });
      })
      .catch(function (err) {
        console.error('[aroma-auto-add] lookup failed for', scentLabel, err);
        return null;
      });
  }

  function addCompanionProduct(scentLabel) {
    if (inFlight[scentLabel]) return;
    inFlight[scentLabel] = true;

    findVariantIdForScent(scentLabel).then(function (variantId) {
      if (!variantId) {
        inFlight[scentLabel] = false;
        return;
      }

      isAlreadyInCart(variantId).then(function (alreadyThere) {
        if (alreadyThere) {
          console.log('[aroma-auto-add] companion already in cart, skipping:', scentLabel);
          inFlight[scentLabel] = false;
          return;
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: QUANTITY,
                properties: { _auto_added: 'Matched ' + scentLabel + ' aroma selection' }
              }
            ]
          })
        })
          .then(function (response) {
            inFlight[scentLabel] = false;
            if (!response.ok) {
              return response.json().then(function (err) {
                console.error('[aroma-auto-add] failed to add companion product (likely out of stock or unavailable):', scentLabel, err);
              });
            }
            console.log('[aroma-auto-add] companion product added:', scentLabel);

            ['cart:refresh', 'cart:updated', 'cart:build'].forEach(function (name) {
              document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
            });
          })
          .catch(function (err) {
            inFlight[scentLabel] = false;
            console.error('[aroma-auto-add] network error while adding companion product', err);
          });
      });
    });
  }

  document.addEventListener(
    'change',
    function (event) {
      if (isAromaRadio(event.target) && event.target.checked) {
        console.log('[aroma-auto-add] aroma selected:', event.target.value);
        addCompanionProduct(event.target.value);
      }
    },
    true
  );

  document.addEventListener('DOMContentLoaded', function () {
    var preselected = document.querySelector(
      'input[type="radio"][id^="' + RADIO_ID_PREFIX + '"]:checked'
    );
    if (preselected) {
      console.log('[aroma-auto-add] aroma pre-selected on load:', preselected.value);
      addCompanionProduct(preselected.value);
    }
  });
})();
